import { useState } from "react";
import { ResetFieldConfig } from "react-hook-form";
import { TypedFilter } from "odata-query";
import { calendarService } from "@miplatform/api";
import { DATE_TIME_FORMATS, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  CalendarEventType,
  CalendarSetStatus,
  IApiRequest,
  IApiResponse,
  ICalendarConfig,
  ICalendarSet,
  IUserCapacity,
  PlatformModules,
} from "@miplatform/types";
import {
  createUUID,
  formatDate,
  getDate,
  sortByDisplayOrder,
} from "@miplatform/utils";
import {
  ICalendarSetData,
  ICalendarSetDuplicateOptions,
  ICalendarWorkCapacityConfig,
} from "../../views/Config/Calendars/ConfigCalendarSetList.types";
import { useUserCapacities } from "../useUserCapacities/useUserCapacities";

const getCalendarSetsUUID = createUUID();
const getCalendarSetUUID = createUUID();
const getRecentCalendarSetsUUID = createUUID();
const deleteCalendarSetUUID = createUUID();

export const useCalendarSets = ({
  module,
  eventType,
  debouncedSearch = "",
  reset = () => {},
  includeDrafts = false,
}: IUseCalendarSetsPayload = {}) => {
  const stateManager = useComponentState();
  const [calendarSets, setCalendarSets] = useState<ICalendarSet[]>([]);
  const [calendarSetDetails, setCalendarSetDetails] =
    useState<ICalendarSetData | null>(null);
  const [recentCalendarSets, setRecentCalendarSets] = useState<ICalendarSet[]>(
    []
  );
  const [filter, setFilter] = useState<TypedFilter<ICalendarSet>>({});
  const [refresh, setRefresh] = useState(false);
  const { getUserCapacities, updateDefaultUserCapacities } =
    useUserCapacities();

  const formatUserCapacities = (userCapacities: IUserCapacity[]) =>
    userCapacities.reduce((acc, item) => {
      const { userId, orderTemplateId } = item;

      if (!acc[userId]) {
        acc[userId] = {};
      }

      acc[userId][orderTemplateId] = item;

      return acc;
    }, {} as { [userId: string]: { [orderTemplateId: string]: ICalendarWorkCapacityConfig } });

  const generateUserCapacitiesArray = (map: {
    [userId: string]: {
      [workBaseTemplateId: string]: ICalendarWorkCapacityConfig;
    };
  }) =>
    Object.entries(map)
      .flatMap(([userId, templatesMap]) =>
        Object.values(templatesMap).map((config) => ({
          userId,
          orderTemplateId: config.orderTemplateId,
          displayOrder: config.displayOrder,
          amount: config.amount,
        }))
      )
      .filter((item) => Boolean(item.amount));

  const resetCalendarSet = (
    data: ICalendarSet,
    userCapacities: IUserCapacity[] = []
  ) => {
    const today = formatDate(getDate(), DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD);
    const formattedData = {
      ...data,
      workCapacities: formatUserCapacities(userCapacities),
      dayStartTime: getDate(`${today} ${data.dayStartTime}`),
      dayEndTime: getDate(`${today} ${data.dayEndTime}`),
      calendarConfigs: sortByDisplayOrder(data.calendarConfigs).map(
        (config: ICalendarConfig) => ({
          ...config,
          ...(config.followUpDefaultOffsetHours && {
            followUpDefaultOffsetHours:
              config.followUpDefaultOffsetHours.toString(),
          }),
          ...(config.followUpMaxOffsetHours && {
            followUpMaxOffsetHours: config.followUpMaxOffsetHours.toString(),
          }),
        })
      ),
    } as unknown as ICalendarSetData;
    reset(formattedData);
    setCalendarSetDetails(formattedData);
  };

  const getCalendarSet = (id: string) =>
    stateManager.run(
      async () =>
        calendarService.calendarSets.getById({
          id,
        }),
      getCalendarSetUUID
    );

  const getCalendarSetDetails = async (id: string) => {
    const [calendarSetResponse, userCapacitiesResponse] = await Promise.all([
      getCalendarSet(id),
      getUserCapacities({ id }),
    ]);

    if (calendarSetResponse.status === HTTP_STATUSES.OK) {
      resetCalendarSet(
        calendarSetResponse.data,
        userCapacitiesResponse.status === HTTP_STATUSES.OK
          ? userCapacitiesResponse.data
          : []
      );
    }
  };

  const getCalendarSets = ({
    top,
    skip,
    sort,
    filters = {},
    params,
    signal,
  }: IApiRequest = {}) => {
    stateManager.abort(getCalendarSetsUUID);
    const combinedFilters = {
      ...filter,
      ...filters,
      ...(module && { module: { eq: module } }),
      ...(eventType && {
        eventType: { eq: eventType },
      }),
      ...(debouncedSearch && { name: { contains: debouncedSearch } }),
    };
    return stateManager.run(
      async () =>
        calendarService.calendarSets.getAll({
          sort: sort ?? "name asc",
          top,
          skip,
          filters: combinedFilters,
          signal,
          params: {
            ...params,
            includeDrafts,
          },
        }),
      getCalendarSetsUUID
    );
  };

  const getRecentCalendarSets = ({
    top,
    skip,
    sort,
    filters = {},
    params,
    signal,
  }: IApiRequest = {}) => {
    stateManager.abort(getRecentCalendarSetsUUID);
    const combinedFilters = {
      ...filter,
      ...filters,
      ...(module && { module: { eq: module } }),
      ...(eventType && {
        eventType: { eq: eventType },
      }),
      ...(debouncedSearch && { name: { contains: debouncedSearch } }),
    };
    return stateManager.run(
      async () =>
        calendarService.calendarSets.getAll({
          sort: sort ?? "name asc",
          top,
          skip,
          filters: combinedFilters,
          params: {
            ...params,
            includeDrafts,
          },
          signal,
        }),
      getRecentCalendarSetsUUID
    );
  };

  const deleteCalendarSet = async (
    calendarSet: ICalendarSet
  ): Promise<IApiResponse> => {
    const { id, modifiedOn } = calendarSet;
    stateManager.abort(deleteCalendarSetUUID);
    return stateManager.run(async () => {
      const response = await calendarService.calendarSets.delete({
        id,
        data: { modifiedOn },
        toastMessageConfig: {
          success: {
            text: `${calendarSet.name} deleted successfully`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        return response;
      }
      return null;
    }, deleteCalendarSetUUID);
  };

  const updateCalendarSetStatus = (data: Partial<ICalendarSetData>) =>
    stateManager.run(async () => {
      const response = await calendarService.calendarSets.updateStatus({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} status was updated.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
        reset(
          {
            ...data,
            status: data.status,
            modifiedOn: response.data.modifiedOn,
          },
          { keepDirty: true }
        );
        setCalendarSetDetails((prev) =>
          prev
            ? ({
                ...prev,
                status: data.status,
                modifiedOn: response.data.modifiedOn,
              } as ICalendarSetData)
            : prev
        );
      }
    });

  const mapCalendarSetData = (data: ICalendarSetData) => ({
    ...data,
    dayStartTime: formatDate(
      data.dayStartTime,
      DATE_TIME_FORMATS.FORMAT_HH_mm_ss
    ),
    dayEndTime: formatDate(data.dayEndTime, DATE_TIME_FORMATS.FORMAT_HH_mm_ss),
    workTemplateBaseIds: data.workTemplateBases.map((template) => template.id),
    assignableUserIds: data.assignableUsers.map((user) => user.id),
    schedulerUserIds: data.schedulerUsers.map((user) => user.id),
    calendarConfigs: data.calendarConfigs.map((config) => ({
      ...config,
      attachedTrayId: config.attachedTray?.id,
    })),
  });

  const createCalendarSet = (data: ICalendarSetData) => {
    const modifiedData = mapCalendarSetData(data);

    return stateManager.run(async () => {
      return calendarService.calendarSets.create({
        data: modifiedData,
        toastMessageConfig: {
          success: {
            text: `${data.name} was created.`,
          },
        },
      });
    });
  };

  const createCalendarSetWithCapacities = async (data: ICalendarSetData) => {
    const response = await createCalendarSet(data);
    const capacities = generateUserCapacitiesArray(data.workCapacities);

    if (response.status === HTTP_STATUSES.OK && capacities.length > 0) {
      updateDefaultUserCapacities({
        calendarSetId: response.data.id,
        capacities,
      });
    }

    return response;
  };

  const duplicateCalendarSet = (
    data: ICalendarSet,
    options: ICalendarSetDuplicateOptions
  ) => {
    const trayIdsMap = data.trays.reduce((acc, tray) => {
      const newId = createUUID();
      acc[tray.id] = newId;
      return acc;
    }, {} as Record<string, string>);
    const modifiedData = {
      ...data,
      id: createUUID(),
      name: `Copy of ${data.name}`,
      status: CalendarSetStatus.Draft,
      ...(options.tabsAndTrays
        ? {
            trays: data.trays.map((tray) => ({
              ...tray,
              id: trayIdsMap[tray.id],
            })),
            calendarConfigs: data.calendarConfigs.map((config, index) => ({
              ...config,
              id: createUUID(),
              attachedTrayId: trayIdsMap[config.attachedTray?.id as string],
              ...(index === 0 && {
                name: `Copy of ${config.name}`,
              }),
            })),
          }
        : {
            trays: data.trays
              .filter(({ isDefault }) => isDefault)
              .map((tray) => ({
                ...tray,
                id: trayIdsMap[tray.id],
              })),
            calendarConfigs: [{ ...data.calendarConfigs[0], id: createUUID() }],
          }),
      workTemplateBaseIds: data.workTemplateBases.map(
        (template) => template.id
      ),
      ...(options.assignableUsers && {
        assignableUserIds: data.assignableUsers.map((user) => user.id),
      }),
      ...(options.schedulerUsers && {
        schedulerUserIds: data.schedulerUsers.map((user) => user.id),
      }),
      serviceAreas: options.serviceArea
        ? data.serviceAreas.map((area) => ({
            ...area,
            id: createUUID(),
          }))
        : [],
    };

    return stateManager.run(async () => {
      const response = await calendarService.calendarSets.create({
        data: modifiedData,
        toastMessageConfig: {
          success: {
            text: `${data.name} was duplicated.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  const updateCalendarSet = (data: ICalendarSetData) => {
    const modifiedData = mapCalendarSetData(data);

    return stateManager.run(async () => {
      const response = await calendarService.calendarSets.update({
        id: modifiedData.id,
        data: modifiedData,
        toastMessageConfig: {
          success: {
            text: `${data.name} was updated.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        const capacities = generateUserCapacitiesArray(data.workCapacities);

        if (capacities.length > 0) {
          const capacitiesResponse = await updateDefaultUserCapacities({
            calendarSetId: response.data.id,
            capacities,
          });
          resetCalendarSet(
            response.data,
            capacitiesResponse.status === HTTP_STATUSES.OK
              ? capacitiesResponse.data
              : []
          );
        } else {
          resetCalendarSet(response.data);
        }
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    calendarSets,
    recentCalendarSets,
    filter,
    refresh,
    calendarSetDetails,
    getCalendarSetDetails,
    setCalendarSets,
    setRecentCalendarSets,
    setFilter,
    getCalendarSets,
    getRecentCalendarSets,
    createCalendarSet,
    updateCalendarSet,
    deleteCalendarSet,
    createCalendarSetWithCapacities,
    duplicateCalendarSet,
    updateCalendarSetStatus,
  };
};

interface IUseCalendarSetsPayload {
  module?: PlatformModules;
  eventType?: CalendarEventType;
  debouncedSearch?: string;
  reset?: (
    data?: Partial<ICalendarSetData>,
    options?: ResetFieldConfig<ICalendarSetData>
  ) => void;
  includeDrafts?: boolean;
}
